"use client";

/**
 * Generic feature-page walkthrough-video slot (ticket G3-10).
 *
 * Source-parameterised version that supersedes the per-feature
 * <RamsWalkthroughVideo> / <CppWalkthroughVideo> (G3-2 / G3-3), which were
 * near-identical and hard-coded their PostHog event names + copy per feature.
 *
 * When `videoUrl` is set, renders a click-to-load embed facade — we do NOT
 * autoplay an iframe; the facade keeps the page light and only mounts the embed
 * after an explicit play. When it is empty, renders a tasteful placeholder
 * pointing at a completed sample instead of fabricating a video.
 *
 * Fires `feature_video_played` (fire-and-forget via usePostHog) the first time
 * the visitor presses play. The placeholder sample CTA is the generic
 * <FeatureTrackedCta> (event="feature_sample_viewed",
 * location="walkthrough_placeholder").
 */

import { useCallback, useState } from "react";
import { PlayCircle } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { FeatureTrackedCta } from "@/components/marketing/feature-acquisition-cta";

interface FeatureWalkthroughVideoProps {
  /** PostHog event source, e.g. "features_rams". */
  source: string;
  /** Embed URL; empty string renders the placeholder instead of an embed. */
  videoUrl: string;
  /** Section heading, e.g. "Watch a RAMS come together". */
  heading: string;
  /** Supporting blurb under the heading. */
  blurb: string;
  /** Where the placeholder sample CTA points (defaults to /samples). */
  sampleHref?: string;
  /** Placeholder sample CTA label. */
  sampleLabel?: string;
}

export function FeatureWalkthroughVideo({
  source,
  videoUrl,
  heading,
  blurb,
  sampleHref = "/samples",
  sampleLabel = "See a completed sample →",
}: FeatureWalkthroughVideoProps) {
  const posthog = usePostHog();
  const [playing, setPlaying] = useState(false);
  const hasVideo = videoUrl.length > 0;

  const handlePlay = useCallback(() => {
    // Fire-and-forget — matching the page's other captures.
    posthog?.capture?.("feature_video_played", {
      source,
      location: "walkthrough",
    });
    setPlaying(true);
  }, [posthog, source]);

  return (
    <section className="px-4 py-16 sm:px-6 sm:py-20">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
          {heading}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-muted">{blurb}</p>

        <div className="mt-10">
          {hasVideo ? (
            playing ? (
              <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-border bg-black">
                <iframe
                  src={`${videoUrl}${videoUrl.includes("?") ? "&" : "?"}autoplay=1`}
                  title="The Site Book walkthrough"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 h-full w-full"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={handlePlay}
                aria-label="Play the walkthrough video"
                className="group relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-xl border border-border bg-navy"
              >
                <span className="absolute inset-0 bg-gradient-to-br from-navy to-navy/80" />
                <PlayCircle
                  className="relative h-16 w-16 text-white transition-transform group-hover:scale-110"
                  aria-hidden="true"
                />
              </button>
            )
          ) : (
            <div className="flex flex-col items-center rounded-xl border border-dashed border-border bg-surface-muted px-6 py-12">
              <PlayCircle className="h-12 w-12 text-accent" aria-hidden="true" />
              <p className="mt-4 max-w-md text-base text-muted">
                The full walkthrough video is on its way. In the meantime, see a
                completed document created by The Site Book.
              </p>
              <div className="mt-6">
                <FeatureTrackedCta
                  href={sampleHref}
                  source={source}
                  location="walkthrough_placeholder"
                  event="feature_sample_viewed"
                  className="inline-flex min-h-[48px] items-center justify-center rounded-lg bg-accent px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-accent-dark"
                >
                  {sampleLabel}
                </FeatureTrackedCta>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
